// Ownership for the named (`room:join`) and generic (`join`, room) protocols.
// This service has no React or browser dependencies.
export interface SocketTransport {
    connected: boolean
    on: (event: string, callback: (...args: any[]) => void) => any
    off: (event: string, callback: (...args: any[]) => void) => any
    emit: (event: string, ...args: any[]) => any
}

type Protocol = 'named' | 'generic'
type Room = { named: number, generic: number, namedJoined: boolean, genericJoined: boolean }
const registries = new WeakMap<SocketTransport, ReturnType<typeof createRooms>>()

function createRooms(local: SocketTransport) {
    const rooms = new Map<string, Room>()
    let ready = true
    const send = (protocol: Protocol, room: string, action: 'join' | 'leave') => {
        // Socket.IO buffers application emits while disconnected. A released
        // lease must never leave a queued join for the next connection.
        if (!local.connected) return
        if (protocol === 'named') local.emit(`${room}:${action}`)
        else local.emit(action, room)
    }
    const join = (name: string, room: Room) => {
        if (!ready || !local.connected) return
        if (room.named && !room.namedJoined) {
            send('named', name, 'join')
            room.namedJoined = true
            room.genericJoined = false
        } else if (!room.named && room.generic && !room.genericJoined) {
            send('generic', name, 'join')
            room.genericJoined = true
        }
    }
    return {
        isReady: () => ready && local.connected,
        acquire(protocol: Protocol, name: string) {
            if (!name) return () => {}
            let room = rooms.get(name)
            if (!room) {
                room = { named: 0, generic: 0, namedJoined: false, genericJoined: false }
                rooms.set(name, room)
            }
            room[protocol]++
            join(name, room)
            let released = false
            return () => {
                if (released) return
                released = true
                room[protocol]--
                if (protocol === 'named' && !room.named && room.namedJoined) {
                    send('named', name, 'leave')
                    room.namedJoined = false
                    // A generic consumer may still own the same physical room.
                    join(name, room)
                } else if (!room.generic && !room.namedJoined && room.genericJoined) {
                    send('generic', name, 'leave')
                    room.genericJoined = false
                }
                if (!room.named && !room.generic) rooms.delete(name)
            }
        },
        beforeLogin() {
            ready = false
            // Some named handlers track info.user, rather than a captured user.
            // Release membership while the server still has the old login.
            rooms.forEach((room, name) => {
                if (room.namedJoined) send('named', name, 'leave')
                room.namedJoined = false
            })
        },
        disconnected() {
            ready = false
            rooms.forEach(room => { room.namedJoined = room.genericJoined = false })
        },
        resume() {
            ready = true
            rooms.forEach((room, name) => join(name, room))
        },
    }
}

export function socketRooms(local: SocketTransport) {
    let rooms = registries.get(local)
    if (!rooms) { rooms = createRooms(local); registries.set(local, rooms) }
    return rooms
}

export type SocketAuth = { user?: string, token?: string }
const credentials = ({ user, token }: SocketAuth): SocketAuth => ({ user, token })
const sameAuth = (a?: SocketAuth, b?: SocketAuth) => !!a && !!b && a.user === b.user && a.token === b.token

// login:done has no request ID. Keep one login in flight, and publish/rejoin
// only after the latest credentials have been acknowledged.
export function createSocketSession(local: SocketTransport, auth: SocketAuth, onReady: () => void) {
    const rooms = socketRooms(local)
    let active = true, connected = false
    let latest = credentials(auth), accepted: SocketAuth, pending: SocketAuth
    let retry: ReturnType<typeof setTimeout>, retryDelay = 1000
    const cancelRetry = () => { if (retry !== undefined) clearTimeout(retry); retry = undefined }
    const login = () => {
        if (!active || !connected || pending || retry !== undefined || sameAuth(accepted, latest)) return
        rooms.beforeLogin()
        pending = credentials(latest)
        local.emit('login', pending)
    }
    const onConnect = () => {
        if (!active) return
        connected = true
        cancelRetry(); retryDelay = 1000
        pending = accepted = undefined
        rooms.disconnected()
        login()
    }
    const onDisconnect = () => {
        connected = false
        cancelRetry()
        pending = accepted = undefined
        rooms.disconnected()
    }
    const onLogin = (result?: { retry?: boolean }) => {
        if (!active || !connected || !pending) return
        const attempted = pending
        pending = undefined
        if (result?.retry) {
            accepted = undefined
            if (!sameAuth(attempted, latest)) { cancelRetry(); retryDelay = 1000; login(); return }
            retry = setTimeout(() => { retry = undefined; login() }, retryDelay)
            retryDelay = Math.min(retryDelay * 2, 30000)
            return
        }
        cancelRetry(); retryDelay = 1000
        accepted = attempted
        if (!sameAuth(accepted, latest)) { login(); return }
        local.emit('init')
        rooms.resume()
        onReady()
    }
    rooms.disconnected()
    local.on('connect', onConnect)
    local.on('disconnect', onDisconnect)
    local.on('login:done', onLogin)
    if (local.connected) onConnect()
    return {
        updateAuth(auth: SocketAuth) {
            if (!sameAuth(latest, auth)) { cancelRetry(); retryDelay = 1000 }
            latest = credentials(auth); login()
        },
        dispose() {
            if (!active) return
            active = false
            cancelRetry()
            pending = accepted = undefined
            local.off('connect', onConnect)
            local.off('disconnect', onDisconnect)
            local.off('login:done', onLogin)
            rooms.disconnected()
        },
    }
}
